import {
  Box,
  Button,
  Card,
  Heading,
  HStack,
  Image,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  ArrowRight,
  Brain,
  Check,
  Cpu,
  FileWarning,
  type LucideIcon,
  Network,
  Scaling,
  Shield,
  ShieldAlert,
  TrendingUp,
  Unplug,
  Users,
} from 'lucide-react'
import clusterArchitecture from '@/assets/ClusterArchitecture.png'
import clusterInteroperability from '@/assets/clusterInteroperability.png'
import clusterScalability from '@/assets/clusterScalability.png'
import hivemqLogo from '@/assets/hivemq-bee-neg.svg'
import NumberTicker from '@/components/magicui/number-ticker'
import { InsightCard, PageHeader, RevealStack, SectionHeading } from '@/components/shell'

export const Route = createFileRoute('/_app/hivemqpilars')({
  component: HiveMQPilarsPage,
})

interface FeatureItemProps {
  title: string
  description: string
}

function FeatureItem({ title, description }: FeatureItemProps) {
  return (
    <HStack gap={3} align="flex-start">
      <Box p={1} bg="green.100" borderRadius="full" flexShrink={0} mt={1}>
        <Check size={14} color="var(--chakra-colors-green-600)" />
      </Box>
      <VStack align="start" gap={0}>
        <Text fontWeight="bold">{title}</Text>
        <Text fontSize="sm" color="content.secondary">
          {description}
        </Text>
      </VStack>
    </HStack>
  )
}

interface PainPointProps {
  accent: string
  colorPalette: string
  icon: LucideIcon
  title: string
  description: string
  tag?: string
}

const painPoints: PainPointProps[] = [
  // NOTE: the quotes below are illustrative, written to match the voice of the originals.
  // Replace them with real discovery-call quotes before this page is shown to a prospect -
  // an invented customer quote is worse than no quote.
  {
    accent: 'red.solid',
    colorPalette: 'red',
    icon: Unplug,
    title: 'Message Loss Under Load',
    description:
      'The broker accepts more than it can persist, and the overflow is silent. Nobody discovers the gap until a report does not reconcile. “We know we lost data, we just cannot tell you how much.”',
  },
  {
    accent: 'orange.solid',
    colorPalette: 'orange',
    icon: FileWarning,
    title: 'Upgrades Mean Downtime',
    description:
      'Patching the messaging layer needs a maintenance window, so it waits - and a broker that is months behind on fixes is carrying risk nobody signed off. “We only touch it during the summer shutdown.”',
  },
  {
    accent: 'purple.solid',
    colorPalette: 'purple',
    icon: Scaling,
    title: 'The Pilot Becomes the Ceiling',
    description:
      'A broker sized for one line will not carry the site, and scaling it means re-architecting the thing everything else now depends on. “It was fine until we connected the second plant.”',
  },
  {
    accent: 'blue.solid',
    colorPalette: 'blue',
    icon: Network,
    title: 'Broker Sprawl',
    description:
      'Every site stood up its own broker on its own version with its own security posture. There is no single place to see who is connected or what is flowing. “Each plant runs their own - we are not really sure what version.”',
  },
  {
    accent: 'teal.solid',
    colorPalette: 'teal',
    icon: ShieldAlert,
    title: 'Connection-Level Security',
    description:
      'Shared credentials, no per-client authorisation, and certificates nobody owns. Proving which device published what, and when, is guesswork. “Everything uses the same username.”',
    tag: 'Pharma',
  },
]

function HiveMQPilarsPage() {
  const navigate = useNavigate()

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        centered
        title="Enterprise Broker"
        subtitle="The MQTT broker at the core of HiveMQ, built for zero message loss, zero-downtime upgrades, and proven scale in mission-critical operations."
      />

      <RevealStack align="stretch" gap={10}>
        {/* Hero identity */}
        <VStack gap={5} textAlign="center">
          <Image src={hivemqLogo} alt="HiveMQ" h="90px" objectFit="contain" />
          <HStack gap={2} flexWrap="wrap" justify="center">
            <Box px={3} py={1} bg="brand.solid" borderRadius="full">
              <Text fontSize="sm" fontWeight="medium" color="gray.900">
                Reliability
              </Text>
            </Box>
            <Box px={3} py={1} bg="brand.solid" borderRadius="full">
              <Text fontSize="sm" fontWeight="medium" color="gray.900">
                Scalability
              </Text>
            </Box>
            <Box px={3} py={1} bg="brand.solid" borderRadius="full">
              <Text fontSize="sm" fontWeight="medium" color="gray.900">
                Security
              </Text>
            </Box>
          </HStack>
        </VStack>

        {/* Stats row */}
        <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
          <Card.Root transition="all 0.3s" _hover={{ transform: 'translateY(-4px)', shadow: 'lg' }}>
            <Card.Body p={5}>
              <HStack gap={3}>
                <Box p={2} bg="brand.solid" borderRadius="lg">
                  <Shield size={20} />
                </Box>
                <VStack align="start" gap={0}>
                  <Text fontSize="2xl" fontWeight="bold" lineHeight="1">
                    Zero
                  </Text>
                  <Text fontSize="xs" color="content.secondary">
                    Message Loss
                  </Text>
                </VStack>
              </HStack>
            </Card.Body>
          </Card.Root>

          <Card.Root transition="all 0.3s" _hover={{ transform: 'translateY(-4px)', shadow: 'lg' }}>
            <Card.Body p={5}>
              <HStack gap={3}>
                <Box p={2} bg="brand.solid" borderRadius="lg">
                  <Scaling size={20} />
                </Box>
                <VStack align="start" gap={0}>
                  <Text fontSize="2xl" fontWeight="bold" lineHeight="1">
                    <NumberTicker value={200} />M
                  </Text>
                  <Text fontSize="xs" color="content.secondary">
                    Active Clients
                  </Text>
                </VStack>
              </HStack>
            </Card.Body>
          </Card.Root>

          <Card.Root transition="all 0.3s" _hover={{ transform: 'translateY(-4px)', shadow: 'lg' }}>
            <Card.Body p={5}>
              <HStack gap={3}>
                <Box p={2} bg="brand.solid" borderRadius="lg">
                  <Users size={20} />
                </Box>
                <VStack align="start" gap={0}>
                  <Text fontSize="2xl" fontWeight="bold" lineHeight="1">
                    <NumberTicker value={6000} />+
                  </Text>
                  <Text fontSize="xs" color="content.secondary">
                    Customers
                  </Text>
                </VStack>
              </HStack>
            </Card.Body>
          </Card.Root>
        </SimpleGrid>

        {/* Why Do Anything */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="Why Do Anything?"
            description="What fails, stalls, or is lost if the messaging layer stays as it is?"
          />
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
            <Card.Root
              transition="all 0.3s"
              _hover={{ transform: 'translateY(-4px)', shadow: 'lg' }}
            >
              <Card.Body p={5}>
                <VStack align="start" gap={3}>
                  <Box p={2} bg="brand.solid" borderRadius="lg">
                    <Cpu size={20} />
                  </Box>
                  <VStack align="start" gap={1}>
                    <Text fontWeight="bold">OT Engineer / Site Operations</Text>
                    <Text fontSize="sm" color="content.secondary">
                      A broker restart stops the line. Latency spikes show up as phantom faults on
                      the HMI. When messages go missing, the shift report is wrong and nobody can
                      say why.
                    </Text>
                  </VStack>
                </VStack>
              </Card.Body>
            </Card.Root>

            <Card.Root
              transition="all 0.3s"
              _hover={{ transform: 'translateY(-4px)', shadow: 'lg' }}
            >
              <Card.Body p={5}>
                <VStack align="start" gap={3}>
                  <Box p={2} bg="brand.solid" borderRadius="lg">
                    <Network size={20} />
                  </Box>
                  <VStack align="start" gap={1}>
                    <Text fontWeight="bold">IT / Integration Architect</Text>
                    <Text fontSize="sm" color="content.secondary">
                      A broker per site, each on its own version and upgrade path. Custom bridging
                      code to move data between them. Every new plant adds another thing to patch,
                      monitor and secure.
                    </Text>
                  </VStack>
                </VStack>
              </Card.Body>
            </Card.Root>

            <Card.Root
              transition="all 0.3s"
              _hover={{ transform: 'translateY(-4px)', shadow: 'lg' }}
            >
              <Card.Body p={5}>
                <VStack align="start" gap={3}>
                  <Box p={2} bg="brand.solid" borderRadius="lg">
                    <TrendingUp size={20} />
                  </Box>
                  <VStack align="start" gap={1}>
                    <Text fontWeight="bold">Transformation Leader</Text>
                    <Text fontSize="sm" color="content.secondary">
                      The pilot worked on one line and will not survive the rollout. Scaling means
                      re-architecting the messaging layer everything else already depends on, so the
                      second site costs as much as the first.
                    </Text>
                  </VStack>
                </VStack>
              </Card.Body>
            </Card.Root>

            <Card.Root
              transition="all 0.3s"
              _hover={{ transform: 'translateY(-4px)', shadow: 'lg' }}
            >
              <Card.Body p={5}>
                <VStack align="start" gap={3}>
                  <Box p={2} bg="brand.solid" borderRadius="lg">
                    <Brain size={20} />
                  </Box>
                  <VStack align="start" gap={1}>
                    <Text fontWeight="bold">Data Scientist / AI Engineer</Text>
                    <Text fontSize="sm" color="content.secondary">
                      The history has gaps and duplicates, because the broker dropped under load and
                      the retry logic double-published. Time spent proving the data is sound is time
                      not spent on the model.
                    </Text>
                  </VStack>
                </VStack>
              </Card.Body>
            </Card.Root>
          </SimpleGrid>
        </VStack>

        {/* Industrial Pain Points */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="Industrial Pain Points"
            description="What we hear from customers running MQTT in production today."
          />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
            {painPoints.map((painPoint) => (
              <InsightCard
                key={painPoint.title}
                accent={painPoint.accent}
                colorPalette={painPoint.colorPalette}
                icon={painPoint.icon}
                badgeLabel="Pain Point"
                title={painPoint.title}
                description={painPoint.description}
                tag={painPoint.tag}
              />
            ))}
          </SimpleGrid>
        </VStack>

        {/* Why HiveMQ */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="Why HiveMQ?"
            description="Three proof points that set the HiveMQ broker apart"
          />

          {/* Proof Point 1: Stream */}
          <Card.Root w="100%">
            <Card.Body p={6}>
              <SimpleGrid columns={{ base: 1, lg: 2 }} gap={8} alignItems="center">
                <VStack align="start" gap={4}>
                  <Heading size="md">Stream data at enterprise scale without fail</Heading>
                  <Text fontSize="sm" color="content.secondary">
                    Build a resilient real-time data backbone for always-on, mission-critical
                    operations.
                  </Text>
                  <VStack align="start" gap={3}>
                    <FeatureItem
                      title="Masterless cluster architecture"
                      description="No single point of failure  -  continuous operation guaranteed."
                    />
                    <FeatureItem
                      title="Zero message loss"
                      description="Persistent messaging and replication to disk with true Quality of Service."
                    />
                    <FeatureItem
                      title="Zero downtime upgrades"
                      description="Nodes seamlessly upgraded without service interruption."
                    />
                    <FeatureItem
                      title="Proven at scale"
                      description="Benchmarked to 200M active clients with 1.8B messages/hour."
                    />
                  </VStack>
                </VStack>
                <Box display={{ base: 'none', lg: 'flex' }} justifyContent="center">
                  <Image
                    src={clusterArchitecture}
                    alt="Cluster Architecture"
                    maxH="300px"
                    objectFit="contain"
                    borderRadius="lg"
                  />
                </Box>
              </SimpleGrid>
            </Card.Body>
          </Card.Root>

          {/* Proof Point 2: Unify */}
          <Card.Root w="100%">
            <Card.Body p={6}>
              <SimpleGrid columns={{ base: 1, lg: 2 }} gap={8} alignItems="center">
                <Box display={{ base: 'none', lg: 'flex' }} justifyContent="center">
                  <Image
                    src={clusterScalability}
                    alt="Cluster Scalability"
                    maxH="300px"
                    objectFit="contain"
                    borderRadius="lg"
                  />
                </Box>
                <VStack align="start" gap={4}>
                  <Heading size="md">Grow without re-architecting</Heading>
                  <Text fontSize="sm" color="content.secondary">
                    Add sites, devices and throughput to the cluster you already run, rather than
                    replacing it.
                  </Text>
                  <VStack align="start" gap={3}>
                    <FeatureItem
                      title="Elastic scaling"
                      description="Linear scalability from 2 to 100+ nodes with consistent hashing  -  add capacity without a redesign."
                    />
                    <FeatureItem
                      title="Add and remove nodes live"
                      description="The cluster rebalances itself while running, so growth never needs a maintenance window."
                    />
                    <FeatureItem
                      title="Shared subscriptions"
                      description="Scale consumers out across instances without duplicating traffic or inventing a queue."
                    />
                    <FeatureItem
                      title="One broker for every site"
                      description="A single version, one upgrade path and one security posture, instead of a broker per plant."
                    />
                  </VStack>
                </VStack>
              </SimpleGrid>
            </Card.Body>
          </Card.Root>

          {/* Proof Point 3: Feed AI */}
          <Card.Root w="100%">
            <Card.Body p={6}>
              <SimpleGrid columns={{ base: 1, lg: 2 }} gap={8} alignItems="center">
                <VStack align="start" gap={4}>
                  <Heading size="md">Secure it, watch it, connect it</Heading>
                  <Text fontSize="sm" color="content.secondary">
                    Everything needed to run messaging as production infrastructure rather than a
                    component somebody has to babysit.
                  </Text>
                  <VStack align="start" gap={3}>
                    <FeatureItem
                      title="Per-client authentication and authorisation"
                      description="TLS and mTLS, with topic-level permissions per client instead of one shared credential."
                    />
                    <FeatureItem
                      title="Enterprise identity integration"
                      description="Plug into the directory and certificate authority you already run."
                    />
                    <FeatureItem
                      title="Enterprise observability"
                      description="Prometheus, JMX and OpenTelemetry  -  every client, session and queue visible."
                    />
                    <FeatureItem
                      title="Integration ecosystem"
                      description="Kafka, Kinesis, Pub/Sub, Event Hub, Snowflake, Databricks and more, without custom bridging code."
                    />
                  </VStack>
                </VStack>
                <Box display={{ base: 'none', lg: 'flex' }} justifyContent="center">
                  <Image
                    src={clusterInteroperability}
                    alt="Broker Interoperability"
                    maxH="300px"
                    objectFit="contain"
                    borderRadius="lg"
                  />
                </Box>
              </SimpleGrid>
            </Card.Body>
          </Card.Root>
        </VStack>

        {/* Why Now */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="Why Now?"
            description="What makes delaying the messaging layer riskier or more expensive today than before?"
          />
          <SimpleGrid columns={{ base: 1, md: 3 }} gap={6}>
            <Card.Root
              transition="all 0.3s"
              _hover={{ transform: 'translateY(-4px)', shadow: 'lg' }}
              h="100%"
            >
              <Card.Body p={6}>
                <VStack align="start" gap={3}>
                  <Box p={2} bg="brand.solid" borderRadius="lg">
                    <TrendingUp size={20} />
                  </Box>
                  <Text fontWeight="bold">More depends on the stream every quarter</Text>
                  <Text fontSize="sm" color="content.secondary">
                    Each new use case makes the messaging layer harder to take down. The window in
                    which it can be replaced without a shutdown is closing, not widening.
                  </Text>
                </VStack>
              </Card.Body>
            </Card.Root>

            <Card.Root
              transition="all 0.3s"
              _hover={{ transform: 'translateY(-4px)', shadow: 'lg' }}
              h="100%"
            >
              <Card.Body p={6}>
                <VStack align="start" gap={3}>
                  <Box p={2} bg="brand.solid" borderRadius="lg">
                    <Scaling size={20} />
                  </Box>
                  <Text fontWeight="bold">Volumes are outgrowing yesterday's brokers</Text>
                  <Text fontSize="sm" color="content.secondary">
                    Device counts and message rates only go up. A broker sized for last year's
                    traffic becomes the constraint on everything built after it.
                  </Text>
                </VStack>
              </Card.Body>
            </Card.Root>

            <Card.Root
              transition="all 0.3s"
              _hover={{ transform: 'translateY(-4px)', shadow: 'lg' }}
              h="100%"
            >
              <Card.Body p={6}>
                <VStack align="start" gap={3}>
                  <Box p={2} bg="brand.solid" borderRadius="lg">
                    <Shield size={20} />
                  </Box>
                  <Text fontWeight="bold">Broker sprawl only compounds</Text>
                  <Text fontSize="sm" color="content.secondary">
                    Every plant that stands up its own broker adds a version, a security posture and
                    a failure mode. Consolidating five is a project; consolidating fifty is a
                    programme.
                  </Text>
                </VStack>
              </Card.Body>
            </Card.Root>
          </SimpleGrid>
        </VStack>

        {/* CTA */}
        <Card.Root bg="bg.subtle">
          <Card.Body p={8}>
            <HStack justify="space-between" align="center" flexWrap="wrap" gap={4}>
              <VStack align="start" gap={1}>
                <Heading size="lg">Ready to explore more?</Heading>
                <Text color="content.secondary">
                  Discover how HiveMQ Edge connects operational technology at the source
                </Text>
              </VStack>
              <Button size="lg" onClick={() => navigate({ to: '/hivemqedge' })}>
                Explore HiveMQ Edge
                <ArrowRight size={18} />
              </Button>
            </HStack>
          </Card.Body>
        </Card.Root>
      </RevealStack>
    </Box>
  )
}
